import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";
import { atom, useAtom } from "jotai";
import React from "react";
import { API } from "../API";
import { STRINGS } from "../strings";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { translate } from "../translate";
import { doNothing } from "../utils/doNothing";
import { CHEVRON_RIGHT_ICON, DOCUMENT_DOLLAR, GIFT_ICON } from "../utils/svgs_new";
import FinishOnboardingModal from "./FinishOnboardingModal";
import { AppButton } from "./Generic/AppButton";
import { AppIcon } from "./Generic/AppIcon";

interface Props {
  caregiver: Pick<AppCaregiver, "id" | "firstName" | "phoneNumbers">;
  getApplicationBanner: API["getApplicationBanner"];
  hideFinishOnboardingModal: boolean;
  isClockBannerShown: boolean;
  onClick: () => void;
}

export type UseFinishOnboardingReturnType = ReturnType<typeof useFinishOnboarding>;

export function useFinishOnboarding(params: { getApplicationBanner: API["getApplicationBanner"] }) {
  const query = useQuery({
    queryKey: ["application_banner"],
    queryFn: params.getApplicationBanner,
  });

  if (query.isLoading || query.data?.show !== true) {
    return { show: false } as const;
  }

  const type =
    query.data.onboardingStage === "onboarding2" && query.data.caregiverStatus === "ACTIVE"
      ? "onboarding2"
      : "regular";

  return { type: type, show: true } as const;
}

function FinishOnboardingSection(props: Props) {
  const { show, type } = useFinishOnboarding({ getApplicationBanner: props.getApplicationBanner });

  if (!show) {
    return <></>;
  }
  return <FinishOnboardingReady {...props} type={type} />;
}

const wasModalOpenedAtom = atom(false);

export const useFinishOnboardingModal = (params: {
  caregiver: Pick<AppCaregiver, "firstName">;
  onClickNext: () => void;
  onRequestClose: () => void;
}) => {
  const [isModalOpen, setModalOpen] = React.useState(false);
  const [wasOpened, setWasOpened] = useAtom(wasModalOpenedAtom);

  React.useEffect(() => {
    if (wasOpened) {
      return;
    }

    const timeout = setTimeout(() => {
      setModalOpen(true);
      setWasOpened(true);
    }, 0);

    return () => clearTimeout(timeout);
  }, [wasOpened, setWasOpened]);

  const handleClickClose = () => {
    setModalOpen(false);
    params.onRequestClose();
  };

  const handleClickNext = () => {
    setModalOpen(false);
    params.onClickNext();
  };

  const modalProps: React.ComponentProps<typeof FinishOnboardingModal> = {
    caregiver: params.caregiver,
    isOpen: isModalOpen,
    onRequestClose: handleClickClose,
    onClickNext: handleClickNext,
  };

  return { modalProps };
};

function FinishOnboardingReady(props: Props & { type: "regular" | "onboarding2" }) {
  return props.type === "onboarding2" ? (
    <FinishOnboardingReadyStage2 {...props} />
  ) : (
    <FinishOnboardingReadyFirstStages {...props} />
  );
}

function FinishOnboardingReadyFirstStages(props: Props) {
  const { modalProps } = useFinishOnboardingModal({
    caregiver: props.caregiver,
    onClickNext: props.onClick,
    onRequestClose: props.onClick,
  });

  return (
    <FinishOnboardingReady.Styled>
      <OnboardingButton
        isClockBannerShown={props.isClockBannerShown}
        onClick={modalProps.onRequestClose}
      >
        <AppIcon icon={GIFT_ICON} fill="white" size={16} />
        <span>{translate(STRINGS.FINISH_ONBOARDING_AND_GET_DOLLARS)}</span>
        <AppIcon icon={CHEVRON_RIGHT_ICON} fill="white" size={16} />
      </OnboardingButton>
      <FinishOnboardingModal
        {...modalProps}
        isOpen={!props.hideFinishOnboardingModal && modalProps.isOpen}
      />
    </FinishOnboardingReady.Styled>
  );
}

function FinishOnboardingReadyStage2(props: Props) {
  const { modalProps } = useFinishOnboardingModal({
    caregiver: props.caregiver,
    onClickNext: props.onClick,
    onRequestClose: doNothing,
  });

  return (
    <FinishOnboardingReady.Styled>
      <OnboardingButton isClockBannerShown={true} onClick={props.onClick}>
        <AppIcon icon={DOCUMENT_DOLLAR} fill="white" size={16} />
        <span>{translate(STRINGS.COMPLETE_ALL_DOCUMENTS_BEFORE_PAYROLL)}</span>
        <AppIcon icon={CHEVRON_RIGHT_ICON} fill="white" size={16} />
      </OnboardingButton>
      <FinishOnboardingModal.Stage2 {...modalProps} />
    </FinishOnboardingReady.Styled>
  );
}

const gradient = keyframes`
    0% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
    100% {
        background-position: 0% 50%;
    }
`;

const shadow = keyframes`
    0% { transform: none; opacity: 1;  }
    100% { transform: scaleY(1.8) scaleX(1.12); opacity: 0; }
`;

const OnboardingButton = styled(AppButton)<{ isClockBannerShown: boolean }>`
  background: linear-gradient(-45deg, #ee7752, #e73c7e, #23a6d5, #23d5ab);
  background-size: 400% 400%;
  animation: ${gradient} 10s ease infinite;
  position: relative;
  width: 100%;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) =>
      props.isClockBannerShown
        ? `unset`
        : `linear-gradient(-45deg, #ee7752, #e73c7e, #23a6d5, #23d5ab)`};
    animation: ${gradient} 10s ease infinite, ${shadow} 2s ease infinite;
    z-index: -1;
    border-radius: 8px;
    transform: scaleY(1.4) scaleX(1.06);
  }
`;

FinishOnboardingReady.Styled = styled.div``;

export default FinishOnboardingSection;
